import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Save, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { contentAPI } from '../../services/api';

const SECTIONS = [
  { key: 'whySolutions', title: '"Why Buy Renewable Energy Solutions?" Section', testId: 'why-solutions-heading', newCard: { title: '', description: '', icon: 'TrendingUp' }, fields: ['icon', 'description'] },
  { key: 'whyConsultation', title: '"Why Buy Renewable Energy Consultation?" Section', testId: 'why-consultation-heading', newCard: { title: '', description: '', color: 'emerald' }, fields: ['description'] },
  { key: 'whySwitchNow', title: '"Why Switch to Renewable Energy Now?" Section', testId: 'why-switch-heading', newCard: { title: '', points: [], color: 'emerald' }, fields: ['points'] },
];

const Field = ({ label, children }) => (
  <div>
    <label className="text-sm text-gray-600 mb-1 block">{label}</label>
    {children}
  </div>
);

const CardEditor = ({ card, idx, fields, onChange, onRemove }) => (
  <div className="p-4 bg-gray-50 rounded-lg space-y-3">
    <div className="flex items-center justify-between">
      <h4 className="font-medium text-gray-800">Card {idx + 1}</h4>
      <Button size="sm" variant="destructive" onClick={onRemove}><Trash2 className="w-3 h-3" /></Button>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Field label="Title"><Input value={card.title || ''} onChange={e => onChange('title', e.target.value)} /></Field>
      {fields.includes('icon') && (
        <Field label="Icon (TrendingUp, CheckCircle, Award, Sun, Zap)"><Input value={card.icon || ''} onChange={e => onChange('icon', e.target.value)} /></Field>
      )}
    </div>
    {fields.includes('description') && (
      <Field label="Description"><Textarea rows={3} value={card.description || ''} onChange={e => onChange('description', e.target.value)} /></Field>
    )}
    {fields.includes('points') && (
      <Field label="Bullet Points (one per line)">
        <Textarea rows={4} value={(card.points || []).join('\n')} onChange={e => onChange('points', e.target.value.split('\n').filter(s => s.trim()))} />
      </Field>
    )}
  </div>
);

const SectionEditor = ({ config, section, onChange }) => {
  const cards = section?.cards || [];
  const setCards = (next) => onChange({ ...section, cards: next });
  return (
    <Card>
      <CardHeader><CardTitle>{config.title}</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Heading"><Input value={section?.heading || ''} onChange={e => onChange({ ...section, heading: e.target.value })} data-testid={config.testId} /></Field>
          <Field label="Heading Highlight (colored text)"><Input value={section?.headingHighlight || ''} onChange={e => onChange({ ...section, headingHighlight: e.target.value })} /></Field>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-700">Cards</p>
            <Button size="sm" variant="outline" onClick={() => setCards([...cards, { ...config.newCard }])}><Plus className="w-4 h-4 mr-1" /> Add Card</Button>
          </div>
          {cards.map((card, idx) => (
            <CardEditor
              key={`${config.key}-${idx}`}
              card={card}
              idx={idx}
              fields={config.fields}
              onChange={(field, value) => setCards(cards.map((c, i) => (i === idx ? { ...c, [field]: value } : c)))}
              onRemove={() => setCards(cards.filter((_, i) => i !== idx))}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const HomepageSectionsEditor = () => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const res = await contentAPI.get('homepage_sections'); setData(res.data || {}); }
    catch { toast.error('Failed to load'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try { await contentAPI.update('homepage_sections', data); toast.success('Homepage sections saved!'); }
    catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-8 max-w-4xl">
      {SECTIONS.map(config => (
        <SectionEditor key={config.key} config={config} section={data[config.key]} onChange={next => setData(prev => ({ ...prev, [config.key]: next }))} />
      ))}
      <Button onClick={save} disabled={saving} className="w-full" data-testid="save-homepage-sections">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save Homepage Sections
      </Button>
    </div>
  );
};

export default HomepageSectionsEditor;
